/**
 * The words for the server's enumerated values.
 *
 * Kept in one place because the same six skill templates and three engines are offered by the
 * persona binding editor, the local-thinker form and (later) the flow node inspector — three
 * surfaces that must not each invent their own wording for "read_only". The *values* are the
 * server's and are never translated; only the labels and the one-line explanations are.
 */
type Translate = (key: string) => string;

/** Mirrors the backend `SkillCatalog` templates. Order is least to most capable. */
export function skillTemplateLabel(template: string, t: Translate): string {
  switch (template) {
    case "conversational":
      return t("Conversational");
    case "read_only":
      return t("Read-only");
    case "author":
      return t("Author");
    case "researcher":
      return t("Researcher");
    case "orchestrator":
      return t("Orchestrator");
    case "full_power":
      return t("Full power");
    default:
      return template;
  }
}

/** What the template actually permits — the part that decides whether it is safe to grant. */
export function skillTemplateHint(template: string, t: Translate): string {
  switch (template) {
    case "conversational":
      return t("No tools at all — discussion only.");
    case "read_only":
      return t("Search and read. It never changes anything.");
    case "author":
      return t("Read, plus writing and editing documents.");
    case "researcher":
      return t("Read, plus looking things up on the web.");
    case "orchestrator":
      return t("Author, plus running flows and tasks.");
    case "full_power":
      return t("Everything, including shell access. Internal use only.");
    default:
      return "";
  }
}

/**
 * How a facet acts when it is relevant (thinker persona). Mirrors the server's
 * `ThinkerFacet.Pursuits`; the value is the server's, the words are for people.
 */
export function pursuitLabel(pursuit: string, t: Translate): string {
  switch (pursuit) {
    case "question":
      return t("Asks questions");
    case "propose":
      return t("Proposes");
    case "verify":
      return t("Checks the facts");
    case "protect":
      return t("Guards against harm");
    case "simplify":
      return t("Simplifies");
    case "connect":
      return t("Connects ideas");
    default:
      return pursuit;
  }
}

/** Engines a thinker can be pinned to. The value is the server's; the label is for people. */
export function providerLabel(provider: string, t: Translate): string {
  switch (provider) {
    case "mlx":
      return t("Local (MLX)");
    case "ollama":
      return t("Local (Ollama)");
    case "openai":
      return "OpenAI";
    case "anthropic":
      return "Anthropic";
    case "human":
      return t("Person");
    default:
      return provider;
  }
}
